import React from "react";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Link as LinkIcon, Pencil, Trash } from "@phosphor-icons/react";
import { CreatorStatusBadge } from "@/components/StatusBadge";
import { SortHead, PlatformBadge, useSortedList, fmtInt, fmtCur } from "./utils";
import { LiveCell } from "./LiveCell";

export function ProspectTable({ list, onEdit, onDelete, onCreatorUpdated, muted }) {
  const { sorted, sort, toggle } = useSortedList(list);
  if (list.length === 0) {
    return (
      <Card className="p-12 text-center border border-dashed border-slate-300 shadow-none text-sm text-slate-500">
        No creators here yet.
      </Card>
    );
  }
  return (
    <Card className="border border-slate-200 shadow-none overflow-hidden">
      <div className="overflow-auto max-h-[70vh]">
        <Table>
          <TableHeader className="bg-slate-50 sticky top-0 z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
            <TableRow>
              <SortHead label="Creator" k="username" sort={sort} onToggle={toggle} />
              <SortHead label="Followers" k="followers" sort={sort} onToggle={toggle} className="text-right" />
              <SortHead label="Initial cost" k="first_cost" sort={sort} onToggle={toggle} className="text-right" />
              <SortHead label="City" k="city" sort={sort} onToggle={toggle} />
              <SortHead label="Language" k="language" sort={sort} onToggle={toggle} />
              <TableHead>Phone · Email</TableHead>
              <TableHead>Status</TableHead>
              <SortHead label="Notes" k="notes" sort={sort} onToggle={toggle} className="min-w-[220px]" />
              <TableHead className="text-right w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((c) => (
              <TableRow key={c.id} className={`hover:bg-slate-50/60 ${muted ? "opacity-60" : ""}`} data-testid={`creator-row-${c.id}`}>
                <TableCell>
                  {c.link ? (
                    <a
                      href={c.link}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-slate-900 hover:text-blue-600 hover:underline inline-flex items-center gap-1"
                      data-testid={`open-creator-${c.id}`}
                    >
                      @{c.username}{c.platform ? <PlatformBadge p={c.platform} /> : null}
                      <LinkIcon size={11} className="opacity-50" />
                    </a>
                  ) : (
                    <div className="font-medium text-slate-900" data-testid={`open-creator-${c.id}`}>@{c.username}{c.platform ? <PlatformBadge p={c.platform} /> : null}</div>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">{fmtInt(c.followers)}</TableCell>
                <TableCell className="text-right font-mono text-xs font-semibold text-slate-900">
                  {Number(c.first_cost || 0) ? fmtCur(c.first_cost) : <span className="text-slate-400 font-normal">—</span>}
                </TableCell>
                <TableCell className="text-slate-700 text-xs">{c.city || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell className="text-slate-700 text-xs">{c.language || <span className="text-slate-400">—</span>}</TableCell>
                <TableCell className="text-slate-700 text-xs">
                  <div>{c.phone || "—"}</div>
                  <div className="text-slate-500">{c.email || "—"}</div>
                </TableCell>
                <TableCell><CreatorStatusBadge value={c.status} /></TableCell>
                <TableCell className="min-w-[220px]">
                  <LiveCell creator={c} field="notes" type="text" onUpdated={onCreatorUpdated} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => onEdit(c)} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded" data-testid={`edit-${c.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => onDelete(c)} className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded" data-testid={`delete-${c.id}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
